import { useState, useCallback } from 'react';
import dayjs from 'dayjs';
import { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import { calculateEndDateFromSession } from '../../../utils/date';
import { notification } from '../../../utils/messaging';
import {
  SESSION_MAPPING,
  BOOKING_TYPE_MAPPING,
  SMALL_GEO_TYPES,
} from './constants';
import { getMonthRangeForFlexibleSearch, isValidDateSelected } from './DateSelectionSection';

/**
 * Returns the area of the supplied bounds
 * @param bounds {{northeast: {lat: number, lng: number}, southwest: {lat: number, lng: number}}} The area bounds in the format
 * @returns {number} The area in km^2
 */
function calculateBoundsArea(bounds) {
  const latKm = Math.abs(bounds.northeast.lat - bounds.southwest.lat) * 111.32;

  const midLat = (bounds.northeast.lat + bounds.southwest.lat) / 2;

  const lonKm =
    Math.abs(bounds.northeast.lng - bounds.southwest.lng) *
    111.32 *
    Math.cos((midLat * Math.PI) / 180);

  return latKm * lonKm;
}

/**
 * Converts a Google [LatLngBounds](https://developers.google.com/maps/documentation/javascript/reference/coordinates#LatLngBounds) object to an explicit bounds Object (literal)
 * @param {LatLngBounds} geoBounds
 * @returns {{northeast: {lat: number, lng: number}, southwest: {lat: number, lng: number}}|null}
 */
function convertGeoBounds(geoBounds) {
  if (!geoBounds) {
    return null;
  }
  return {
    northeast: {
      lat: geoBounds.getNorthEast().lat(),
      lng: geoBounds.getNorthEast().lng(),
    },
    southwest: {
      lat: geoBounds.getSouthWest().lat(),
      lng: geoBounds.getSouthWest().lng(),
    },
  };
}

export const transformToApiPayload = (searchState) => {
  const isFlexible =
    searchState.dateMethod === 'Flexible Search' &&
    searchState?.selectedMonths?.length;

  let payload = {
    location: {
      lat: searchState.selectedLocation?.coordinates?.lat || 0,
      lng: searchState.selectedLocation?.coordinates?.lng || 0,
    },
    bounds:
      searchState.selectedLocation?.type === 'area'
        ? searchState.selectedLocation?.bounds
        : undefined,
    start_date: '',
    end_date: '',
    time:
      SESSION_MAPPING[searchState.selectedSession] ||
      searchState.selectedSession ||
      '',
    booking_type: '',
    anglers: searchState.anglers || 0,
    guests: searchState.nonAnglers || 0,
    trips_abroad: searchState.trips_abroad || false,
    date_type: isFlexible ? 'flexible' : 'exact',
  };

  if (isFlexible) {
    const [firstMonth, lastMonth] = getMonthRangeForFlexibleSearch(searchState);

    payload.start_date = firstMonth
      ? firstMonth.startOf('month').format('YYYY-MM-DD')
      : null;
    payload.end_date = lastMonth
      ? lastMonth.endOf('month').format('YYYY-MM-DD')
      : null;
  } else if (searchState.bookingDates && searchState.bookingDates[0]) {
    const dateRange = searchState.bookingDates[0];
    if (dateRange.startDate && dayjs(dateRange.startDate).isValid()) {
      payload.start_date = dayjs(dateRange.startDate).format('YYYY-MM-DD');
    }
    if (dateRange.endDate && dayjs(dateRange.endDate).isValid()) {
      payload.end_date = dayjs(dateRange.endDate).format('YYYY-MM-DD');
    }
  }

  const selectedBookingTypes = Object.keys(searchState.bookingTypes || {})
    .filter((key) => searchState.bookingTypes[key])
    .map((key) => BOOKING_TYPE_MAPPING[key])
    .filter(Boolean);

  payload.booking_type = selectedBookingTypes.join(',');

  if (payload?.start_date) {
    const start = dayjs(payload.start_date);
    if (start.isValid() && start.isBefore(dayjs())) {
      // Set start date to today in case of flexible month queries
      payload.start_date = dayjs().format('YYYY-MM-DD');
    }
  }

  return payload;
};

export const useSearchState = (initialState, options) => {
  const [dateSearchState, setDateSearchState] = useState(initialState);
  const [locationSearchValue, setLocationSearchValue] = useState('');

  // Update nested state using dot notation path
  const updateDateSearchState = useCallback((path, value) => {
    setDateSearchState((prev) => {
      const newData = { ...prev };
      const keys = path.split('.');
      let obj = newData;

      for (let i = 0; i < keys.length - 1; i++) {
        obj[keys[i]] = { ...obj[keys[i]] };
        obj = obj[keys[i]];
      }

      obj[keys[keys.length - 1]] =
        typeof value === 'function' ? value(obj[keys[keys.length - 1]]) : value;

      // Special handling for session selection - auto-update dates
      if (path === 'selectedSession') {
        const currentBookingDates = newData.bookingDates;
        let startDate = null;

        if (
          currentBookingDates &&
          currentBookingDates[0] &&
          currentBookingDates[0].startDate
        ) {
          startDate = currentBookingDates[0].startDate;
        } else {
          startDate = new Date();
        }

        const endDate = calculateEndDateFromSession(startDate, value);

        newData.bookingDates = [
          {
            startDate: startDate,
            endDate: endDate,
            key: 'selection',
          },
        ];
      }

      return newData;
    });
  }, []);

  // Dates picked on the calendar replace a quick session choice; the search uses the dates either way.
  const handleDateChange = useCallback((selectedRange) => {
    setDateSearchState((prev) => ({
      ...prev,
      bookingDates: [selectedRange, ...prev.bookingDates.slice(1)],
      selectedSession: null,
    }));
  }, []);

  // Increment counter
  const increment = useCallback((key) => {
    setDateSearchState((prev) => ({
      ...prev,
      [key]: prev[key] + 1,
    }));
  }, []);

  // Decrement counter
  const decrement = useCallback((key) => {
    setDateSearchState((prev) => ({
      ...prev,
      [key]: prev[key] > 0 ? prev[key] - 1 : 0,
    }));
  }, []);

  // Handle location selection from autocomplete
  const handleLocationSelect = useCallback(
    async (address) => {
      try {
        const results = await geocodeByAddress(address);
        const result = results[0];
        const latLng = await getLatLng(result);
        const country = result?.address_components?.find?.(
          (c) =>
            (c.types ?? []).includes('country') || c?.long_name === 'Europe' || c?.long_name === 'United Kingdom'
        )?.long_name;
        const bounds = convertGeoBounds(
          result?.geometry?.viewport || result?.geometry?.bounds
        );
        const MAX_SMALL_THRESHOLD_SQ_KM = 10000;
        const MIN_SMALL_THRESHOLD_SQ_KM = 1000;
        const boundsArea = calculateBoundsArea(bounds);
        const isSmallPlace =
          (result?.types ?? []).some((t) => SMALL_GEO_TYPES.has(t)) ||
          boundsArea < MIN_SMALL_THRESHOLD_SQ_KM;
        const useProximitySearch =
          isSmallPlace && boundsArea < MAX_SMALL_THRESHOLD_SQ_KM;

        const locationData = {
          address: address,
          coordinates: latLng,
          bounds: bounds,
          type: useProximitySearch ? 'point' : 'area',
        };
        const ukCountries = [
          'England',
          'Wales',
          'Scotland',
          'Northern Ireland',
          'Ireland',
          'United Kingdom',
          'UK',
          'Great Britain',
        ];
        updateDateSearchState('selectedLocation', locationData);
        updateDateSearchState(
          'trips_abroad',
          country && !ukCountries.includes(country)
        );
        setLocationSearchValue('');

        // Call onLocationSelected callback if provided
        if (options?.onLocationSelected) {
          options.onLocationSelected();
        }
        return true;
      } catch (error) {
        console.error('Error selecting location:', error);
        return false;
      }
    },
    [updateDateSearchState, options]
  );

  // Handle location input changes
  const handleLocationChange = useCallback((address) => {
    setLocationSearchValue(address);
  }, []);

  // Handle location search errors
  const handleLocationError = useCallback((status, clearSuggestions) => {
    console.error('Error from Google Maps API', status);
    clearSuggestions();
  }, []);

  // Handle special location types (nearby, abroad)
  const handleSpecialLocationSelect = useCallback(
    (locationType) => {
      if (locationType === 'nearby') {
        if (navigator.geolocation) {
          // An answer can arrive long after the request, once the visitor has moved on.
          const isWanted = () => options?.isNearbyLocationWanted?.() ?? true;
          navigator.geolocation.getCurrentPosition(
            (position) => {
              if (!isWanted()) {
                return;
              }
              const locationData = {
                address: 'Nearby Locations',
                coordinates: {
                  lat: position.coords.latitude,
                  lng: position.coords.longitude,
                },
                type: 'nearby',
              };
              updateDateSearchState('selectedLocation', locationData);
              updateDateSearchState('trips_abroad', false);
              setLocationSearchValue('');

              if (options?.onLocationSelected) {
                options.onLocationSelected();
              }
            },
            (error) => {
              if (!isWanted()) {
                return;
              }
              options?.onNearbyLocationError?.(
                error.code === error.PERMISSION_DENIED
                  ? 'denied'
                  : 'unavailable'
              );
            },
            // Town-level accuracy is plenty for nearby fisheries and avoids waiting for GPS on phones.
            {
              enableHighAccuracy: false,
              timeout: options?.locationTimeoutMs ?? 10000,
              maximumAge: 10 * 60 * 1000,
            }
          );
        } else {
          options?.onNearbyLocationError?.('unsupported');
        }
      } else if (locationType === 'abroad') {
        const locationData = {
          address: 'Trips Abroad',
          coordinates: null,
          type: 'abroad',
        };
        updateDateSearchState('selectedLocation', locationData);
        updateDateSearchState('trips_abroad', true);
        setLocationSearchValue('');

        if (options?.onLocationSelected) {
          options.onLocationSelected();
        }
      }
    },
    [updateDateSearchState, options]
  );

  // Validate search criteria
  const validateSearchCriteria = useCallback((searchState) => {
    const hasLocation = searchState?.selectedLocation?.address;
    const hasValidDates = isValidDateSelected(searchState);

    if (!hasLocation && !hasValidDates) {
      notification.warning({
        description: 'Please select a location and dates to search.',
      });
      return false;
    } else if (!hasLocation) {
      notification.warning({
        description: 'Please select a location to search.',
      });
      return false;
    } else if (!hasValidDates) {
      notification.warning({
        description: 'Please select dates to search.',
      });
      return false;
    } else if (!searchState?.anglers) {
      notification.warning({
        description: 'Minimum of 1 angler.',
      });
      return false;
    }
    return true;
  }, []);

  return {
    dateSearchState,
    setDateSearchState,
    locationSearchValue,
    setLocationSearchValue,
    updateDateSearchState,
    handleDateChange,
    increment,
    decrement,
    handleLocationSelect,
    handleLocationChange,
    handleLocationError,
    handleSpecialLocationSelect,
    transformToApiPayload,
    validateSearchCriteria,
  };
};
